import { GameConfig } from "@/config/GameConfig";
import type { LevelDefinition } from "@/levels/types";
import type { WorldDefinition } from "@/levels/worldTypes";
import { LevelRegistry } from "@/levels/LevelRegistry";
import { WorldRegistry } from "@/levels/WorldRegistry";

export interface LevelStats {
  /**
   * Legacy Score & Stars V1 record (Bite Score only - never Final Score).
   * Preserved forever as internal/historical data - no longer presented
   * to the player as "Best" (see `ScoreV2Storage.getBestFinalScore` for
   * the player-facing Final Score record) and no longer used to compute
   * stars (see `computeCampaignCompletionResult`/`stars.ts`). Kept so
   * existing localStorage data is never deleted or rewritten.
   */
  highScore: number;
  stars: 0 | 1 | 2 | 3;
}

/** What a completed Campaign run reports to `recordLevelResult()` - stars already computed (from completed Final Score, via `computeCampaignCompletionResult`) by the caller, so this module never needs to know the star formula itself. */
export interface CampaignLevelResult {
  /** This run's Bite Score - persisted only into the legacy `highScore` field, never compared against or mixed with Final Score. */
  biteScore: number;
  /** This run's earned stars, already computed from its completed Final Score. */
  stars: 1 | 2 | 3;
}

export interface WorldSummary {
  totalLevels: number;
  completedLevels: number;
  stars: number;
  maxStars: number;
  isUnlocked: boolean;
  isCompleted: boolean;
}

const SCHEMA_VERSION = 3;

export interface ProgressState {
  schemaVersion: 3;
  /** Highest level `order` unlocked so far. Level 1 (order 1) is always unlocked. */
  unlockedOrder: number;
  completedIds: string[];
  /** Per-level best score and stars, keyed by the level's stable id. */
  levelStats: Record<string, LevelStats>;
  /** Stable world ids (see `WorldDefinition.id`) fully completed. */
  completedWorldIds: string[];
}

function defaultState(): ProgressState {
  return { schemaVersion: SCHEMA_VERSION, unlockedOrder: 1, completedIds: [], levelStats: {}, completedWorldIds: [] };
}

interface LegacyV1State {
  unlockedOrder: number;
  completedIds: string[];
}

interface LegacyV2State {
  schemaVersion: 2;
  unlockedOrder: number;
  completedIds: string[];
  levelStats: Record<string, LevelStats>;
  /** Old format: `world-<n>` (e.g. "world-1"). Superseded by stable world ids ("w1", "w2", ...). */
  completedWorldIds: string[];
}

function isV1(value: unknown): value is LegacyV1State {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Partial<LegacyV1State>;
  return (
    typeof candidate.unlockedOrder === "number" &&
    Number.isFinite(candidate.unlockedOrder) &&
    Array.isArray(candidate.completedIds) &&
    candidate.completedIds.every((id) => typeof id === "string")
  );
}

function isV2(value: unknown): value is LegacyV2State {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Partial<LegacyV2State>;
  return (
    candidate.schemaVersion === 2 &&
    typeof candidate.unlockedOrder === "number" &&
    Array.isArray(candidate.completedIds) &&
    typeof candidate.levelStats === "object" &&
    candidate.levelStats !== null &&
    Array.isArray(candidate.completedWorldIds)
  );
}

function isV3(value: unknown): value is ProgressState {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Partial<ProgressState>;
  return (
    candidate.schemaVersion === SCHEMA_VERSION &&
    typeof candidate.unlockedOrder === "number" &&
    Array.isArray(candidate.completedIds) &&
    typeof candidate.levelStats === "object" &&
    candidate.levelStats !== null &&
    Array.isArray(candidate.completedWorldIds)
  );
}

/**
 * Every world whose levels are all present in `completedIds`, in world
 * order. Derived fresh from `completedIds` (never trusted from old
 * storage) so it can never drift out of sync with the level data, and is
 * naturally order-independent - it doesn't matter which order the levels
 * were completed in, only that they all are.
 */
function deriveCompletedWorldIds(completedIds: readonly string[]): string[] {
  const completed = new Set(completedIds);
  return LevelRegistry.worlds()
    .filter((group) => group.levels.length > 0 && group.levels.every((level) => completed.has(level.id)))
    .map((group) => group.world.id);
}

/**
 * The unlocked-order floor implied by `completedWorldIds`: for every
 * world that's fully completed, whatever world comes next (by
 * `WorldDefinition.order`) must have its first level unlocked - even if
 * that world/its levels didn't exist yet when the player originally
 * completed the previous one (exactly the "already beat the whole game,
 * a new world just shipped" migration case).
 */
function unlockedOrderFloor(completedWorldIds: readonly string[]): number {
  let floor = 1;
  for (const worldId of completedWorldIds) {
    const nextWorld = WorldRegistry.nextOf(worldId);
    const firstLevel = nextWorld ? LevelRegistry.worlds().find((g) => g.world.id === nextWorld.id)?.levels[0] : undefined;
    if (firstLevel) floor = Math.max(floor, firstLevel.order);
  }
  return floor;
}

/**
 * Migrates whatever was found in storage to the current (v3) shape,
 * preserving every bit of existing progress:
 * - v1 (`{ unlockedOrder, completedIds }`, no `schemaVersion`) levels that
 *   were already completed get a backfilled high score of 0 and 1 star
 *   (the minimum - real stars need a real score, which v1 never recorded).
 * - v2 levels carry their real `levelStats` straight through.
 * - `completedWorldIds` is always recomputed fresh from `completedIds`
 *   (never trusted from storage) - this both fixes the old `world-<n>`
 *   format and automatically unlocks any new world (e.g. EARTH) whose
 *   prerequisite world was already fully completed before it existed.
 * - Anything unrecognized (corrupt, wrong shape) falls back to a fresh
 *   default state rather than throwing.
 * - Idempotent: migrating an already-v3 state re-applies the same
 *   `unlockedOrderFloor()` normalization below (a no-op if nothing
 *   changed) rather than returning it byte-for-byte unchanged - this is
 *   what makes the "already beat the whole game, a new world just
 *   shipped" case work for a player who is ALREADY on schema v3 (every
 *   real existing player, not just someone migrating from v1/v2): without
 *   this, a v3 save's `unlockedOrder` would stay frozen at whatever the
 *   highest level was when they last played, and `ProgressStorage.isUnlocked()`
 *   would report a newly-added world's first level as locked even though
 *   `completedWorldIds` (and `isWorldUnlocked()`) already correctly says
 *   the world itself is unlocked. Never lowers `unlockedOrder`, never
 *   touches `completedIds`/`levelStats`/`completedWorldIds` - purely a
 *   read-time floor-raise, not a schema change.
 */
function migrate(raw: unknown): ProgressState {
  if (isV3(raw)) {
    return { ...raw, unlockedOrder: Math.max(raw.unlockedOrder, unlockedOrderFloor(raw.completedWorldIds)) };
  }

  const completedIds: string[] = isV2(raw) ? raw.completedIds : isV1(raw) ? raw.completedIds : [];
  const legacyUnlockedOrder: number = isV2(raw) ? raw.unlockedOrder : isV1(raw) ? raw.unlockedOrder : 1;
  const legacyLevelStats: Record<string, LevelStats> = isV2(raw) ? raw.levelStats : {};

  const levelStats: Record<string, LevelStats> = { ...legacyLevelStats };
  for (const id of completedIds) {
    if (!levelStats[id]) levelStats[id] = { highScore: 0, stars: 1 };
  }

  const completedWorldIds = deriveCompletedWorldIds(completedIds);
  const unlockedOrder = Math.max(legacyUnlockedOrder, unlockedOrderFloor(completedWorldIds));

  return { schemaVersion: SCHEMA_VERSION, unlockedOrder, completedIds: [...completedIds], levelStats, completedWorldIds };
}

/**
 * Campaign progress: which level is the furthest unlocked, which levels
 * have been completed, and each level's best score/stars and which
 * worlds are fully completed. Persisted to localStorage so a refresh or
 * closing the browser never resets progress.
 *
 * Deliberately independent of Phaser - safe to unit test in isolation.
 */
export const ProgressStorage = {
  get(): ProgressState {
    try {
      const raw = globalThis.localStorage?.getItem(GameConfig.storage.progressKey);
      if (!raw) return defaultState();
      const parsed = JSON.parse(raw);
      return migrate(parsed);
    } catch {
      // Corrupted data or localStorage unavailable (e.g. private browsing) - fail soft.
      return defaultState();
    }
  },

  save(state: ProgressState): void {
    try {
      globalThis.localStorage?.setItem(GameConfig.storage.progressKey, JSON.stringify(state));
    } catch {
      // Ignore storage failures - the game still works, just without persistence.
    }
  },

  isUnlocked(level: LevelDefinition): boolean {
    const state = this.get();
    if (level.order > state.unlockedOrder) return false;
    const world = WorldRegistry.byId(level.worldId);
    return world ? WorldRegistry.isUnlocked(world, state.completedWorldIds) : false;
  },

  isCompleted(level: LevelDefinition): boolean {
    return this.get().completedIds.includes(level.id);
  },

  getLevelStats(level: LevelDefinition): LevelStats {
    return this.get().levelStats[level.id] ?? { highScore: 0, stars: 0 };
  },

  isWorldUnlocked(worldId: string): boolean {
    const world = WorldRegistry.byId(worldId);
    if (!world) return false;
    return WorldRegistry.isUnlocked(world, this.get().completedWorldIds);
  },

  isWorldCompleted(worldId: string): boolean {
    return this.get().completedWorldIds.includes(worldId);
  },

  getWorldSummary(worldId: string): WorldSummary {
    const world = WorldRegistry.byId(worldId);
    const state = this.get();
    const levels = LevelRegistry.worlds().find((g) => g.world.id === worldId)?.levels ?? [];
    const completedLevels = levels.filter((l) => state.completedIds.includes(l.id)).length;
    const stars = levels.reduce((sum, l) => sum + (state.levelStats[l.id]?.stars ?? 0), 0);
    return {
      totalLevels: levels.length,
      completedLevels,
      stars,
      maxStars: levels.length * 3,
      isUnlocked: world ? WorldRegistry.isUnlocked(world, state.completedWorldIds) : false,
      isCompleted: state.completedWorldIds.includes(worldId),
    };
  },

  /**
   * Records the outcome of a level actually completed (never call this on
   * a game-over - losing must never lower or lock anything already
   * earned). Unlocks the next level, and updates the level's legacy high
   * score and star count without ever decreasing either.
   *
   * Score & Stars V2: `result.stars` arrives already computed by the
   * caller from the run's completed Final Score (see
   * `computeCampaignCompletionResult`) - this module never computes stars
   * itself, so it has no dependency on the star formula. `result.biteScore`
   * is persisted only into the legacy `highScore` field (see
   * `LevelStats.highScore`'s doc) - the two numbers are never compared or
   * mixed.
   *
   * `newlyUnlockedWorld` is set only the moment a world becomes newly
   * completed *and* that unlocks a following world (e.g. finishing THE
   * MOON's last level unlocks EARTH) - the signal the UI uses to show a
   * one-time "EARTH UNLOCKED" celebration. `isCampaignComplete` is true
   * when this was the last level of the last existing world.
   */
  recordLevelResult(
    level: LevelDefinition,
    result: CampaignLevelResult,
  ): {
    stars: 1 | 2 | 3;
    /** This run raised the level's stored star record. */
    isNewStarRecord: boolean;
    /** This run raised the level's legacy Bite Score record - internal/analytics only, never player-facing (see `ScoreV2Storage`'s Final Score record for the player-facing "new high score"). */
    isNewLegacyHighScore: boolean;
    newlyCompletedWorld?: WorldDefinition;
    newlyUnlockedWorld?: WorldDefinition;
  } {
    const state = this.get();

    const completedIds = state.completedIds.includes(level.id) ? state.completedIds : [...state.completedIds, level.id];

    const previous = state.levelStats[level.id] ?? { highScore: 0, stars: 0 };
    const isNewLegacyHighScore = result.biteScore > previous.highScore;
    const stars = Math.max(previous.stars, result.stars) as 1 | 2 | 3;
    const isNewStarRecord = stars > previous.stars;
    const levelStats: Record<string, LevelStats> = {
      ...state.levelStats,
      [level.id]: { highScore: Math.max(previous.highScore, result.biteScore), stars },
    };

    const next = LevelRegistry.nextOf(level.id);
    const unlockedOrder = next ? Math.max(state.unlockedOrder, next.order) : state.unlockedOrder;

    const completedWorldIds = deriveCompletedWorldIds(completedIds);
    const world = WorldRegistry.byId(level.worldId);
    const worldJustCompleted = world && !state.completedWorldIds.includes(world.id) && completedWorldIds.includes(world.id);
    const newlyUnlockedWorld = worldJustCompleted ? WorldRegistry.nextOf(world.id) : undefined;

    this.save({
      schemaVersion: SCHEMA_VERSION,
      unlockedOrder: Math.max(unlockedOrder, unlockedOrderFloor(completedWorldIds)),
      completedIds,
      levelStats,
      completedWorldIds,
    });

    return { stars, isNewStarRecord, isNewLegacyHighScore, newlyCompletedWorld: worldJustCompleted ? world : undefined, newlyUnlockedWorld };
  },

  /** The furthest level the player has unlocked - what CONTINUE resumes. */
  getHighestUnlockedLevel(): LevelDefinition {
    const state = this.get();
    const levels = LevelRegistry.all();
    let highest = LevelRegistry.firstLevel();
    for (const level of levels) {
      if (level.order <= state.unlockedOrder && level.order > highest.order) {
        highest = level;
      }
    }
    return highest;
  },
};
